import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

import {
  fetchAnswerAudio,
  useSaveReview,
  type AnswerReview,
} from "@/api/tests-api";
import { DictationPlayer } from "@/components/tests/DictationPlayer";
import { useAuthStore } from "@/store/auth-store";
import { TestCaseViewer } from "@/components/tests/TestCaseViewer";

/**
 * The candidate's cases, read the way the candidate read them.
 *
 * THE SAME LAYOUT AS THE SITTING, with one substitution. A reviewer
 * scoring "did they describe what is there" is doing the candidate's
 * job a second time, so they get the candidate's screen: the case rail
 * on the left, the stack filling the middle, the clinical history
 * beside it -- and where the candidate had TestRecorder, the reviewer
 * has the playback of what they actually said.
 *
 * IT REPLACED AN INLINE PANEL, and the reason is worth keeping. The
 * images were first shown in a collapsible block inside the screening
 * console, beside the player. That console is a 1240px reading column
 * and the block got roughly half of it -- a viewport too small to read
 * a CT in, with the images scaled down to thumbnails and cut off. A
 * radiologist cannot score what they cannot see, and no amount of
 * arranging fixes a panel that size. This takes the screen.
 *
 * FULL-SCREEN, ABOVE THE CONSOLE, and it closes back to it: the
 * screening is the job and this is a thing you step into and out of
 * while doing it, not a separate destination.
 */
export function CaseReviewer({
  answers,
  initialIndex,
  attemptId,
  canEdit,
  onClose,
}: {
  answers: AnswerReview[];
  initialIndex: number;
  attemptId: string;
  /** False when the screening belongs to another reviewer. */
  canEdit: boolean;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const current = answers[index];
  const save = useSaveReview();

  // The caller's own note on this case. Everyone's are shown; only one
  // is editable, which is the same rule the sheet follows. Matched on
  // the reviewer id rather than a server flag: this is attribution, not
  // permission -- the server still decides who may write.
  const meId = useAuthStore((st) => st.user?.id ?? null);
  const mine = useMemo(
    () => current?.reviews.find((r) => r.reviewer_user_id === meId) ?? null,
    [current, meId],
  );
  const [note, setNote] = useState(mine?.comments ?? "");
  const [saved, setSaved] = useState<"idle" | "saving" | "done">("idle");
  const timer = useRef<number | null>(null);

  // Reset to whatever is stored WHEN THE CASE CHANGES, or a note typed
  // on case 1 would appear to belong to case 2.
  //
  // Keyed on the answer alone. Depending on ``mine.comments`` as well
  // looked more correct and was not: a successful save invalidates the
  // board, the stored comment changes to what was just written, and the
  // effect fired -- wiping "Saved." back to the idle hint before anyone
  // could read it, and clobbering anything typed while the request was
  // in flight.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setNote(mine?.comments ?? "");
    setSaved("idle");
  }, [current?.answer_id]);

  // Debounced, like the candidate's own form: a reviewer types a
  // sentence, not a keystroke, and a PUT per character would be a
  // request storm against a route that upserts.
  const commit = (text: string) => {
    setNote(text);
    if (!current?.answer_id) return;
    if (timer.current !== null) window.clearTimeout(timer.current);
    setSaved("saving");
    timer.current = window.setTimeout(() => {
      save.mutate(
        {
          attemptId,
          answerId: current.answer_id as string,
          body: {
            score: null,
            max_score: null,
            outcome: null,
            comments: text.trim() === "" ? null : text,
          },
        },
        { onSuccess: () => setSaved("done"), onError: () => setSaved("idle") },
      );
    }, 800);
  };

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  // Escape closes, like every other overlay here.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!current) return null;

  return (
    <div
      data-cy="case-reviewer"
      className="fixed inset-0 z-[60] flex flex-col bg-background text-foreground"
    >
      <header className="flex items-center justify-between gap-4 bg-background-topbar px-6 py-3 text-foreground-on-chrome shadow-theme-sm">
        <div className="flex min-w-0 items-center gap-4">
          <span className="truncate text-sm font-medium">
            Reviewing the candidate&rsquo;s cases
          </span>
          <span
            data-cy="review-case-position"
            className="shrink-0 rounded-full bg-background-card px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted"
          >
            Case {index + 1} of {answers.length}
          </span>
        </div>
        <button
          type="button"
          data-cy="btn-close-case-reviewer"
          onClick={onClose}
          className="flex items-center gap-1.5 rounded-md border border-border bg-background-card px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-table-row-hover"
        >
          <X aria-hidden className="h-3.5 w-3.5" />
          Back to the screening
        </button>
      </header>

      <section className="flex flex-1 overflow-hidden">
        {/* The rail, as the candidate had it -- but marking which cases
            were RECORDED rather than which are answered, because a
            silent case is the thing a reviewer is looking for. */}
        <nav
          data-cy="review-case-nav"
          className="flex w-72 shrink-0 flex-col overflow-auto border-r border-border bg-background-card"
        >
          <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-foreground-muted">
            Cases ({answers.filter((a) => a.has_audio).length}/{answers.length}{" "}
            recorded)
          </div>
          <ul>
            {answers.map((a, i) => (
              <li key={a.assignment_case_id}>
                <button
                  type="button"
                  onClick={() => setIndex(i)}
                  className={[
                    "flex w-full items-center gap-2 border-l-2 px-4 py-2 text-left text-sm transition-colors",
                    i === index
                      ? "border-accent bg-background text-foreground"
                      : "border-transparent text-foreground-muted hover:bg-table-row-hover",
                  ].join(" ")}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {a.position + 1}. {a.title}
                  </span>
                  {!a.has_audio && (
                    <span
                      title="No recording for this case"
                      className="shrink-0 text-[10px] font-semibold uppercase text-status-urgent"
                    >
                      silent
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex-1 overflow-auto px-8 py-6">
          <article className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-foreground">
                {current.title}
              </h2>
              <p className="mt-1 text-xs uppercase tracking-wide text-foreground-muted">
                {current.modality || "Case"}
              </p>

              {/* The same 68vh the candidate got. Anything smaller is
                  the panel this replaced. */}
              <div className="mt-4 h-[68vh] min-h-[22rem]">
                {/* Keyed on the case: the viewer holds decoded frames and
                    a scroll position, and stepping on must not inherit
                    either. */}
                <TestCaseViewer
                  key={current.test_case_id}
                  source={{ kind: "library", caseId: current.test_case_id }}
                />
              </div>
            </div>

            <div className="min-w-0 xl:sticky xl:top-6 xl:self-start">
              {current.clinical_history && (
                <p className="mb-4 whitespace-pre-line rounded-lg border border-border bg-background-card p-4 text-sm text-foreground">
                  {current.clinical_history}
                </p>
              )}

              {/* Where the candidate had the recorder. */}
              {current.has_audio && current.answer_id ? (
                <div className="rounded-lg border border-border bg-background-card p-4">
                  <h3 className="text-sm font-medium text-foreground">
                    Their dictation
                  </h3>
                  <div className="mt-3">
                    {/* Remounted per case for the same reason the
                        stepper does it: the player caches the object URL
                        it fetched, and the audio behind it changes. */}
                    <DictationPlayer
                      key={current.answer_id}
                      load={() => fetchAnswerAudio(current.answer_id as string)}
                      durationHint={current.duration_seconds}
                    />
                  </div>
                </div>
              ) : (
                <p className="rounded-lg border border-border bg-background-card p-4 text-sm italic text-foreground-muted">
                  The candidate recorded nothing for this case.
                </p>
              )}

              {/* THE NOTE ON THIS DICTATION, and it is part of the
                  record: the print path carries every case's note, not
                  just the one on screen. Saved against the ANSWER, so
                  it travels with the recording it is about rather than
                  with the sheet as a whole. */}
              <div className="mt-4 rounded-lg border border-border bg-background-card p-4">
                <label
                  className="text-sm font-medium text-foreground"
                  htmlFor="case-review-note"
                >
                  Your comment on this dictation
                </label>
                <textarea
                  id="case-review-note"
                  data-cy="case-review-note"
                  className="sk-input mt-2 min-h-[7rem]"
                  placeholder={
                    canEdit
                      ? "What did they cover, what did they miss, how did it read?"
                      : ""
                  }
                  value={note}
                  disabled={!canEdit || !current.answer_id}
                  onChange={(e) => commit(e.target.value)}
                />
                <p className="mt-1 text-xs text-foreground-muted">
                  {!current.answer_id
                    ? "There is no recording to comment on."
                    : !canEdit
                      ? "This screening belongs to another reviewer."
                      : saved === "saving"
                        ? "Saving…"
                        : saved === "done"
                          ? "Saved. It appears on the printed record."
                          : "Saved as you type, and printed with the sheet."}
                </p>

                {/* Anyone else's note on this case, read-only. */}
                {current.reviews
                  .filter((r) => r.reviewer_user_id !== meId && r.comments)
                  .map((r) => (
                    <p
                      key={r.id}
                      className="mt-3 border-t border-border pt-2 text-xs text-foreground-muted"
                    >
                      <span className="font-medium text-foreground">
                        {r.reviewer_name ?? "A reviewer"}:
                      </span>{" "}
                      {r.comments}
                    </p>
                  ))}
              </div>

              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  type="button"
                  data-cy="btn-review-prev-case"
                  className="sk-btn sk-sm"
                  disabled={index === 0}
                  onClick={() => setIndex((i) => Math.max(0, i - 1))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  data-cy="btn-review-next-case"
                  className="sk-btn sk-sm"
                  disabled={index >= answers.length - 1}
                  onClick={() =>
                    setIndex((i) => Math.min(answers.length - 1, i + 1))
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
